import React, { createContext, useContext, useState, useCallback } from "react";

const FeedbackContext = createContext();

// Track which triggers have been shown in this session
const shownTriggers = new Set();

export const FeedbackProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [triggerType, setTriggerType] = useState("itinerary");
    const [email, setEmail] = useState("");
    const [bookingId, setBookingId] = useState("");

    const showFeedback = useCallback((type, options = {}) => {
        // Prevent showing the same trigger type twice in a session (unless forced)
        if (!options.force && shownTriggers.has(type)) {
            return false;
        }

        shownTriggers.add(type);
        setTriggerType(type);
        setEmail(options.email || "");
        setBookingId(options.bookingId || "");
        setIsOpen(true);
        return true;
    }, []);

    const closeFeedback = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Convenience methods for different triggers
    // force=true bypasses the duplicate check (for manual "Click here" links)
    const showItineraryFeedback = useCallback((force = false) => {
        return showFeedback("itinerary", { force });
    }, [showFeedback]);

    const showBookingFeedback = useCallback((email, bookingId) => {
        return showFeedback("booking", { email, bookingId });
    }, [showFeedback]);

    const showExitFeedback = useCallback(() => {
        return showFeedback("session_exit");
    }, [showFeedback]);

    const value = {
        isOpen,
        triggerType,
        email,
        bookingId,
        showFeedback,
        closeFeedback,
        showItineraryFeedback,
        showBookingFeedback,
        showExitFeedback
    };

    return (
        <FeedbackContext.Provider value={value}>
            {children}
        </FeedbackContext.Provider>
    );
};

export const useFeedback = () => {
    const context = useContext(FeedbackContext);
    if (!context) {
        throw new Error("useFeedback must be used within a FeedbackProvider");
    }
    return context;
};

export default FeedbackContext;
